import { Tabs as TabsPrimitive } from "@base-ui/react/tabs";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { focusStyles } from "@beweegbaas/ui/lib/focus-styles";
import { cnState } from "@beweegbaas/ui/lib/utils";

type TabsContextValue = {
  variant?: "default" | "line";
  mode?: "default" | "success" | "error";
};

const TabsContext = React.createContext<TabsContextValue>({
  variant: "default",
  mode: "default",
});

const tabsListVariants = cva(
  [
    "forced-color-adjust-none scrollable-mask scrollbar-none",
    "flex min-w-0 max-w-full flex-nowrap items-center justify-start",
    "group-data-[orientation=vertical]/tabs:h-fit group-data-[orientation=vertical]/tabs:flex-col",
    "p-0 group-data-[orientation=horizontal]/tabs:h-9 group/tabs-list text-muted-foreground relative",
  ],
  {
    variants: {
      variant: {
        default: "rounded squircle supports-squircle:rounded-full bg-muted z-0 relative p-[3px]",
        line: "gap-x-5 bg-transparent rounded-none",
      },
      mode: {
        default: "",
        success: "bg-green-100 dark:bg-green-950",
        error: "bg-red-100 dark:bg-red-950",
      },
    },
    defaultVariants: {
      variant: "default",
      mode: "default",
    },
  },
);

function Tabs({
  className,
  orientation = "horizontal",
  variant = "default",
  mode = "default",
  ...props
}: TabsPrimitive.Root.Props & VariantProps<typeof tabsListVariants>) {
  const variantValue = variant ?? "default";
  const modeValue = mode ?? "default";
  return (
    <TabsContext.Provider value={{ variant: variantValue, mode: modeValue }}>
      <TabsPrimitive.Root
        data-slot="tabs"
        orientation={orientation}
        className={cnState(
          "group/tabs flex w-full max-w-full min-w-0 gap-2 data-[orientation=horizontal]:flex-col",
          className,
        )}
        {...props}
      />
    </TabsContext.Provider>
  );
}

function TabsList({ className, children, ...props }: TabsPrimitive.List.Props) {
  const { variant = "default" } = React.useContext(TabsContext);
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cnState(tabsListVariants({ variant }), className)}
      {...props}
    >
      {children}
      <TabsIndicator />
    </TabsPrimitive.List>
  );
}

const tabsTriggerVariants = cva(
  [
    "rounded squircle supports-squircle:rounded-full",
    "focus-visible:outline-ring text-foreground/60 hover:text-foreground dark:text-muted-foreground dark:hover:text-foreground inline-flex h-[calc(100%-1px)] items-center justify-center gap-1.5 border-0 text-sm font-medium whitespace-nowrap transition-all group-data-[orientation=horizontal]/tabs:shrink-0 group-data-[orientation=vertical]/tabs:w-full group-data-[orientation=vertical]/tabs:justify-start focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  ],
  {
    variants: {
      variant: {
        default: ["data-active:text-foreground py-1 px-2"],
        line: [
          "pb-3",
          "bg-transparent data-active:bg-transparent dark:data-active:border-transparent dark:data-active:bg-transparent data-active:shadow-none data-active:text-foreground dark:data-active:text-foreground",
        ],
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function TabsTrigger({ className, ...props }: TabsPrimitive.Tab.Props) {
  const { variant = "default" } = React.useContext(TabsContext);
  return (
    <TabsPrimitive.Tab
      data-slot="tabs-trigger"
      className={cnState([tabsTriggerVariants({ variant }), focusStyles], className)}
      {...props}
    />
  );
}

const tabsIndicatorVariants = cva(
  [
    "rounded squircle supports-squircle:rounded-full",
    "absolute transition-all duration-200 ease-in-out group-data-[orientation=horizontal]/tabs:left-0 group-data-[orientation=horizontal]/tabs:translate-x-[var(--active-tab-left)] group-data-[orientation=horizontal]/tabs:w-[var(--active-tab-width)] group-data-[orientation=vertical]/tabs:top-0 group-data-[orientation=vertical]/tabs:translate-y-[var(--active-tab-top)] group-data-[orientation=vertical]/tabs:h-[var(--active-tab-height)]",
  ],
  {
    variants: {
      variant: {
        default: [
          "bg-white shadow",
          "z-[-1] group-data-[orientation=horizontal]/tabs:bottom-[var(--active-tab-bottom)] group-data-[orientation=horizontal]/tabs:h-[var(--active-tab-height)] group-data-[orientation=vertical]/tabs:left-0 group-data-[orientation=vertical]/tabs:w-[var(--active-tab-width)] group-data-[orientation=vertical]/tabs:ml-1",
        ],
        line: [
          "bg-foreground",
          "group-data-[orientation=horizontal]/tabs:bottom-[0px] group-data-[orientation=horizontal]/tabs:h-0.5 group-data-[orientation=vertical]/tabs:left-full group-data-[orientation=vertical]/tabs:w-0.5 group-data-[orientation=vertical]/tabs:ml-1",
        ],
      },
      mode: {
        default: "",
        success: "bg-green-100 dark:bg-green-900",
        error: "bg-red-100 dark:bg-red-900",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function TabsIndicator({ className, ...props }: TabsPrimitive.Indicator.Props) {
  const { variant = "default", mode: modeValue } = React.useContext(TabsContext);
  return (
    <TabsPrimitive.Indicator
      data-slot="tabs-indicator"
      className={cnState(tabsIndicatorVariants({ variant, mode: modeValue }), className)}
      {...props}
    />
  );
}

function TabsContent({ className, ...props }: TabsPrimitive.Panel.Props) {
  return (
    <TabsPrimitive.Panel
      data-slot="tabs-content"
      className={cnState("text-sm outline-none", className)}
      {...props}
    />
  );
}

// TabsPrimitive exported for advanced use cases
export {
  Tabs,
  TabsContent,
  TabsIndicator,
  tabsIndicatorVariants,
  TabsList,
  tabsListVariants,
  TabsPrimitive,
  TabsTrigger,
  tabsTriggerVariants,
};
